import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';

import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { SuperStarComponent } from '../../../app-layout/shared/super-star/super-star.component';
import {
    EntityHistoryDialogComponent,
    EntityHistoryDialogInput,
} from './entity-history-dialog.component';
import { EntityHistorySubjectType } from './entity-history.types';

/**
 * Menu-item variant of the entity-history drop-in. Use inside a `<mat-menu>`:
 *
 *   <mat-menu #menu>
 *       <app-entity-history-menu-item
 *           subjectType="violation"
 *           [subjectId]="violation.id"
 *           [subjectLabel]="violation.label"
 *           [canView]="isSuper"
 *           [showSuperStar]="true"    <!-- when canView == isSuper -->
 *           [showIcon]="false">       <!-- optional: omit the clock icon -->
 *       </app-entity-history-menu-item>
 *   </mat-menu>
 *
 * Caller is responsible for `[canView]` (typically `isSuper` for super-only
 * placements). Set `[showSuperStar]="true"` to render the gold ⭐ next to the
 * label, matching how other super-only menu items are signaled in the app.
 */
@Component({
    selector: 'app-entity-history-menu-item',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatMenuModule, SuperStarComponent],
    template: `
        <button *ngIf="canView"
                type="button"
                mat-menu-item
                (click)="open()">
            <i *ngIf="showIcon"
               class="ph-bold ph-clock-counter-clockwise icon-muted icon-16 entity-history-menu-icon"
               aria-hidden="true"></i>
            <ng-container *ngIf="linkStyle; else plainLabel">
                <a>History
                    <app-super-star *ngIf="showSuperStar"></app-super-star>
                </a>
            </ng-container>
            <ng-template #plainLabel>
                <span>History</span>
                <app-super-star *ngIf="showSuperStar"></app-super-star>
            </ng-template>
        </button>
    `,
    styles: [`
        /* Collapse the host so the projected <button> becomes a direct child
           of <mat-menu> — fixes mat-menu-item sizing/spacing being taller
           than sibling items. */
        :host {
            display: contents;
        }

        /* Nothing here restyles the row, on purpose. This is a REAL mat-menu-item (the
           directive, not class="mat-menu-item"), so it inherits the global v2 menu styling
           — font, 40px min-height, 8px icon gap, padding, hover — from resetMenu exactly
           like its siblings.

           It used to hand-replicate those rules against the bare class, which is why the
           label kept drifting out of line with the items above it: the copy could match the
           mixin's font and gap, but not MDC's .mdc-list-item__primary-text label wrapper, so
           "History" sat at a different offset from every other label. Copying a design system
           by hand always eventually disagrees with it — don't reintroduce that here. */
        .entity-history-menu-icon {
            /* Sized to match the sibling icon-16 glyphs; alignment is the menu's job. */
            width: 16px;
            height: 16px;
            line-height: 1;
        }
    `],
})
export class EntityHistoryMenuItemComponent
{
    @Input() subjectType!: EntityHistorySubjectType;
    @Input() subjectId!: number;
    @Input() subjectLabel?: string;
    @Input() showIcon = true;
    @Input() showSuperStar = false;

    /**
     * Wrap the label in `<a>` so it picks up the surrounding mat-menu's
     * blue link style. Set true when sibling items in the menu use
     * `<a>` wrappers (e.g. single-charge-options, unit-owners-table,
     * member-details). Leave false (default) for menus whose items
     * render plain text (e.g. single-payment-actions, single-recurring-
     * charge-options).
     */
    @Input() linkStyle = false;

    /** Permission gate — pass `true` to render the menu item. */
    @Input() canView = false;

    constructor(private _dialog: MatDialog)
    {
    }

    open(): void
    {
        if (!this.subjectType || !this.subjectId) {
            return;
        }

        const data: EntityHistoryDialogInput = {
            subjectType: this.subjectType,
            subjectId: this.subjectId,
            subjectLabel: this.subjectLabel,
        };

        this._dialog.open(EntityHistoryDialogComponent, {
            width: MatDialogSizes.XL,
            maxWidth: '95vw',
            position: { top: '50px' },
            panelClass: 'entity-history-dialog',
            autoFocus: 'dialog',
            data,
        });
    }
}
